import { cn } from '@commenda/utils';
import React from 'react';
import { Table } from '@tanstack/react-table';

import {
  TableHead as THead,
  TableHeader as THeader,
  TableRow as TRow,
} from '../../Table';
import {
  DATA_GRID_DEFAULT_COLUMN_WIDTH,
  DATA_GRID_HEADER_BAND_HEIGHT,
  DATA_GRID_ROW_HEADER_COLUMN_ID,
  DATA_GRID_ROW_HEADER_WIDTH,
} from '../constants';
import {
  DataGridAlign,
  DataGridClassNames,
  DataGridColumn,
  DataGridColumnGroup,
  DataGridRowHeaderConfig,
} from '../types';
import {
  getDataGridColumnMinWidthStyle,
  getDataGridJustifyClass,
  getDataGridTextAlignClass,
} from '../utils';
import {
  DATA_GRID_COLUMN_SEPARATOR_CLASS_NAMES,
  DATA_GRID_HEADER_CELL_CLASS_NAMES,
  DATA_GRID_HEADER_RULE_CLASS_NAMES,
  DATA_GRID_PINNED_SHADOW_CLASS_NAMES,
  DATA_GRID_ROW_SEPARATOR_CLASS_NAMES,
  DATA_GRID_STICKY_CORNER_CLASS_NAMES,
  DATA_GRID_STICKY_HEADER_CLASS_NAMES,
} from './classNames';

export interface DataGridHeaderProps<TRow> {
  table: Table<TRow>;
  rowHeaderConfig: DataGridRowHeaderConfig<TRow>;
  leafColumnsById: Map<string, DataGridColumn<TRow>>;
  groupColumnsById: Map<string, DataGridColumnGroup<TRow>>;
  isSticky: boolean;
  isRowHeaderPinned: boolean;
  showPinnedColumnShadow: boolean;
  classNames?: DataGridClassNames;
}

const getStickyTopStyle = (
  isSticky: boolean,
  bandIndex: number
): React.CSSProperties | undefined =>
  isSticky ? { top: bandIndex * DATA_GRID_HEADER_BAND_HEIGHT } : undefined;

export function DataGridHeader<TRow>({
  table,
  rowHeaderConfig,
  leafColumnsById,
  groupColumnsById,
  isSticky,
  isRowHeaderPinned,
  showPinnedColumnShadow,
  classNames,
}: DataGridHeaderProps<TRow>) {
  const headerGroups = table.getHeaderGroups();
  const bandCount = headerGroups.length;
  const cornerAlign: DataGridAlign = rowHeaderConfig.align ?? 'left';

  return (
    <THeader className={cn(classNames?.header)}>
      {headerGroups.map((headerGroup, bandIndex) => (
        <TRow key={headerGroup.id}>
          {bandIndex === 0 ? (
            <THead
              rowSpan={bandCount}
              style={{
                ...getStickyTopStyle(isSticky, 0),
                ...getDataGridColumnMinWidthStyle(
                  rowHeaderConfig.width ?? DATA_GRID_ROW_HEADER_WIDTH
                ),
              }}
              className={cn(
                DATA_GRID_HEADER_CELL_CLASS_NAMES,
                'h-auto',
                DATA_GRID_HEADER_RULE_CLASS_NAMES,
                DATA_GRID_COLUMN_SEPARATOR_CLASS_NAMES,
                isSticky && DATA_GRID_STICKY_HEADER_CLASS_NAMES,
                isRowHeaderPinned && DATA_GRID_STICKY_CORNER_CLASS_NAMES,
                isRowHeaderPinned &&
                  showPinnedColumnShadow &&
                  DATA_GRID_PINNED_SHADOW_CLASS_NAMES,
                classNames?.corner
              )}
            >
              <div
                className={cn(
                  'flex items-center',
                  getDataGridJustifyClass(cornerAlign)
                )}
              >
                <span
                  className={cn(
                    'min-w-0 truncate',
                    getDataGridTextAlignClass(cornerAlign)
                  )}
                >
                  {rowHeaderConfig.header}
                </span>
              </div>
            </THead>
          ) : null}

          {headerGroup.headers
            .filter(
              (header) => header.column.id !== DATA_GRID_ROW_HEADER_COLUMN_ID
            )
            .map((header) => {
              const groupColumn = groupColumnsById.get(header.column.id);
              const leafColumn = leafColumnsById.get(header.column.id);
              const isGroupHeader = groupColumn !== undefined;
              const align: DataGridAlign = isGroupHeader
                ? groupColumn.headerAlign ?? 'center'
                : leafColumn?.headerAlign ?? leafColumn?.align ?? 'right';

              if (!isGroupHeader && bandIndex !== header.column.depth) {
                return null;
              }

              return (
                <THead
                  key={header.id}
                  colSpan={header.colSpan}
                  rowSpan={isGroupHeader ? 1 : bandCount - bandIndex}
                  style={{
                    ...getStickyTopStyle(isSticky, bandIndex),
                    ...(isGroupHeader
                      ? undefined
                      : getDataGridColumnMinWidthStyle(
                          leafColumn?.width ?? DATA_GRID_DEFAULT_COLUMN_WIDTH
                        )),
                  }}
                  className={cn(
                    DATA_GRID_HEADER_CELL_CLASS_NAMES,
                    bandIndex === bandCount - 1
                      ? DATA_GRID_HEADER_RULE_CLASS_NAMES
                      : DATA_GRID_ROW_SEPARATOR_CLASS_NAMES,
                    DATA_GRID_COLUMN_SEPARATOR_CLASS_NAMES,
                    isSticky && DATA_GRID_STICKY_HEADER_CLASS_NAMES,
                    isGroupHeader
                      ? classNames?.groupHeaderCell
                      : classNames?.headerCell
                  )}
                >
                  <div
                    className={cn(
                      'flex items-center',
                      getDataGridJustifyClass(align)
                    )}
                  >
                    <span
                      className={cn(
                        'min-w-0 truncate',
                        getDataGridTextAlignClass(align)
                      )}
                    >
                      {isGroupHeader ? groupColumn.header : leafColumn?.header}
                    </span>
                  </div>
                </THead>
              );
            })}
        </TRow>
      ))}
    </THeader>
  );
}
